import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import '@ibk/shared-types';
import { useGrid } from '../hooks/useGrid';
import { useTheme } from '../themes/ThemeProvider';
import styles from './Sidebar.module.css';
const isMenuItemSelected = (item, selectedPath) => {
    if (item.selected)
        return true;
    if (!selectedPath || !item.path)
        return false;
    return item.path === selectedPath || selectedPath.startsWith(item.path + '/');
};
export const Sidebar = ({ menuItems, title = '', logo, collapsed: collapsedProp, onCollapseToggle, selectedPath, hideOnMobile = false, className, style, collapsedIcon, mobileOverlay = false, onMobileClose, }) => {
    const [internalCollapsed, setInternalCollapsed] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const { deviceType } = useGrid();
    const collapsed = collapsedProp !== undefined ? collapsedProp : internalCollapsed;
    const theme = useTheme();
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
    React.useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const isMobileSize = windowWidth <= 1024;
    const shouldShowAsOverlay = mobileOverlay;
    const handleMobileClose = useCallback(() => {
        if (!onMobileClose)
            return;
        setIsAnimating(true);
        setTimeout(() => {
            onMobileClose();
            setIsAnimating(false);
        }, 300);
    }, [onMobileClose]);
    useEffect(() => {
        if (shouldShowAsOverlay && !isAnimating) {
            setIsAnimating(false);
        }
    }, [shouldShowAsOverlay, isAnimating]);
    const handleCollapseToggle = useCallback(() => {
        if (onCollapseToggle)
            onCollapseToggle(!collapsed);
        else
            setInternalCollapsed(c => !c);
    }, [onCollapseToggle, collapsed]);
    useEffect(() => {
        if (deviceType === 'desktop-large') {
            if (collapsed) {
                handleCollapseToggle();
            }
        }
    }, [deviceType, collapsed, handleCollapseToggle]);
    const mainMenuItems = menuItems.filter(item => item.label !== 'Suporte');
    const supportItem = menuItems.find(item => item.label === 'Suporte');
    const sidebarStyle = {
        fontFamily: theme['font-family-base'] ?? "'Source Sans 3', Arial, sans-serif",
        width: collapsed
            ? parseInt(theme['sidebar-width-collapsed'] ?? '72')
            : parseInt(theme['sidebar-width-expanded'] ?? '224'),
        minWidth: collapsed
            ? parseInt(theme['sidebar-width-collapsed'] ?? '72')
            : parseInt(theme['sidebar-width-expanded'] ?? '224'),
        background: theme['sidebar-background'] ?? '#FFFFFF',
        borderRight: `1px solid ${theme['sidebar-border'] ?? '#F2F2F2'}`,
        display: hideOnMobile && isMobileSize ? 'none' : 'flex',
        flexDirection: 'column',
        transition: shouldShowAsOverlay ? 'none' : 'width 0.2s cubic-bezier(.4,0,.2,1)',
        overflow: 'hidden',
        ...(shouldShowAsOverlay && {
            width: parseInt(theme['sidebar-width-expanded'] ?? '224'),
            minWidth: parseInt(theme['sidebar-width-expanded'] ?? '224'),
        }),
        ...style,
    };
    const isCollapsible = deviceType !== 'desktop-large' && !shouldShowAsOverlay;
    const getIconFilter = useCallback((color) => {
        if (color === '#FFFFFF') {
            return 'brightness(0) invert(1)';
        }
        const isDarkColor = color.includes('#1F1F1F') || color.includes('#000') || color.includes('black');
        return isDarkColor ? 'brightness(0)' : 'none';
    }, []);
    const getCollapseIconFilter = useCallback((themeColor) => {
        if (themeColor === '#FFFFFF') {
            return 'brightness(0) invert(1)';
        }
        const isDarkColor = themeColor?.includes('#1F1F1F') || themeColor?.includes('#000') || themeColor?.includes('black');
        return isDarkColor ? 'brightness(0)' : 'none';
    }, []);
    const getDesktopSidebarClassName = (className) => {
        return `${className ?? ''} ${styles['sidebar-desktop']}`;
    };
    const getMobileSidebarClassName = (isActive) => {
        return `${styles['sidebar-mobile']} ${isActive ? styles.active : ''}`;
    };
    const handleOverlayKeyDown = useCallback((event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleMobileClose();
        }
    }, [handleMobileClose]);
    const createIconElement = useCallback((icon, iconColor) => {
        const isValidReactElement = (icon) => {
            return React.isValidElement(icon);
        };
        const getExistingStyle = (element) => {
            if (element.props && typeof element.props === 'object' && 'style' in element.props) {
                const elementProps = element.props;
                return elementProps.style ?? {};
            }
            return {};
        };
        if (!icon || !isValidReactElement(icon)) {
            return icon;
        }
        return React.cloneElement(icon, {
            style: {
                filter: getIconFilter(iconColor),
                ...getExistingStyle(icon),
            },
        });
    }, [getIconFilter]);
    const getMenuItemStyle = useCallback((isSelected) => {
        const itemGap = collapsed ? '0px' : '12px';
        const itemPadding = collapsed ? '16px 8px' : '16px 8px 16px 16px';
        const itemJustifyContent = collapsed ? 'center' : 'flex-start';
        const itemMargin = collapsed ? '0 8px' : '0';
        const itemBackground = isSelected ? (theme['sidebar-item-selected-bg'] ?? '#B23A00') : 'transparent';
        const itemColor = isSelected
            ? (theme['sidebar-item-selected-text'] ?? '#FFFFFF')
            : (theme['sidebar-item-text'] ?? '#1F1F1F');
        return {
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'stretch',
            gap: itemGap,
            padding: itemPadding,
            borderRadius: theme['sidebar-item-border-radius'] ?? '4px',
            background: itemBackground,
            color: itemColor,
            textDecoration: 'none',
            fontWeight: 600,
            fontFamily: theme['font-family-base'] ?? "'Source Sans 3', sans-serif",
            fontSize: '14px',
            lineHeight: '1.43',
            transition: 'all 0.15s cubic-bezier(.4,0,.2,1)',
            cursor: 'pointer',
            justifyContent: itemJustifyContent,
            margin: itemMargin,
            position: 'relative',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
        };
    }, [collapsed, theme]);
    const handleMenuItemClick = useCallback((item) => {
        if (item.onClick) {
            item.onClick();
        }
        if (isMobileSize && onMobileClose) {
            onMobileClose();
        }
    }, [isMobileSize, onMobileClose]);
    const renderMenuItem = useCallback((item, dataTestId) => {
        const isSelected = isMenuItemSelected(item, selectedPath);
        const iconColor = isSelected
            ? (theme['sidebar-item-selected-icon-color'] ?? theme['sidebar-item-selected-text'] ?? '#FFFFFF')
            : (theme['sidebar-item-icon-color'] ?? theme['sidebar-item-text'] ?? '#1F1F1F');
        const iconElement = createIconElement(item.icon, iconColor);
        const itemStyle = getMenuItemStyle(isSelected);
        return (_jsxs(NavLink, { to: item.path, end: true, onClick: () => handleMenuItemClick(item), style: ({ isActive }) => ({
                ...itemStyle,
                background: isActive || isSelected ? (theme['sidebar-item-selected-bg'] ?? '#B23A00') : 'transparent',
            }), title: collapsed ? item.label : undefined, "data-testid": `a-sidebar-link-${dataTestId}`, className: `${styles['sidebar-item']} ${isSelected ? styles['sidebar-item-selected'] : ''}`, children: [_jsx("span", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '16px',
                        width: '16px',
                    }, children: iconElement || (_jsx("svg", { width: '16', height: '16', viewBox: '0 0 16 16', fill: 'none', children: _jsx("circle", { cx: '8', cy: '8', r: '8', fill: theme['sidebar-item-icon-bg'] ?? '#1573D3' }) })) }), !collapsed && _jsx("span", { style: { flex: 1, background: 'transparent' }, children: item.label })] }, item.label));
    }, [selectedPath, theme, collapsed, createIconElement, getMenuItemStyle, handleMenuItemClick]);
    const renderCollapseButton = useCallback((isMobile) => {
        let buttonLabel;
        if (isMobile) {
            buttonLabel = 'Fechar menu';
        }
        else if (collapsed) {
            buttonLabel = 'Expandir menu';
        }
        else {
            buttonLabel = 'Colapsar menu';
        }
        const buttonClick = isMobile ? onMobileClose : handleCollapseToggle;
        return (_jsx("div", { className: styles['sidebar-collapse-button-container'], children: _jsx("button", { "aria-label": buttonLabel, onClick: buttonClick, className: styles['sidebar-collapse-button'], style: {
                    filter: getCollapseIconFilter(theme['sidebar-collapse-icon-color']),
                }, children: collapsedIcon }) }));
    }, [collapsed, onMobileClose, handleCollapseToggle, collapsedIcon, getCollapseIconFilter, theme]);
    const renderLogoSection = useCallback(() => (_jsx("div", { className: styles['sidebar-logo-container'], children: logo ? (_jsx("div", { className: styles['sidebar-logo'], children: _jsx("div", { className: styles['sidebar-logo-image'], children: logo }) })) : (_jsx("span", { className: styles['sidebar-title'], style: {
                fontFamily: theme['font-family-base'] ?? "'Source Sans 3', sans-serif",
                color: theme['sidebar-title-color'] ?? '#000000',
            }, children: title })) })), [logo, title, theme]);
    const renderSidebarHeader = useCallback((isMobile) => {
        let headerClassName;
        if (isMobile) {
            headerClassName = 'expanded';
        }
        else if (collapsed) {
            headerClassName = 'collapsed';
        }
        else {
            headerClassName = 'expanded';
        }
        const showCollapseButton = isMobile || (isCollapsible && !shouldShowAsOverlay);
        const showLogo = !collapsed || isMobile;
        return (_jsxs("div", { className: `${styles['sidebar-header']} ${headerClassName}`, style: {
                borderTop: `4px solid ${theme['sidebar-top-border-color'] ?? theme['sidebar-item-selected-bg'] ?? '#D64000'}`,
            }, children: [showCollapseButton && renderCollapseButton(isMobile), showLogo && renderLogoSection()] }));
    }, [collapsed, theme, isCollapsible, shouldShowAsOverlay, renderCollapseButton, renderLogoSection]);
    const renderSidebarContent = useCallback((isMobile) => {
        let contentClassName;
        if (isMobile) {
            contentClassName = 'expanded';
        }
        else if (collapsed) {
            contentClassName = 'collapsed';
        }
        else {
            contentClassName = 'expanded';
        }
        return (_jsx("div", { className: `${styles['sidebar-content']} ${contentClassName}`, style: {
                gap: theme['sidebar-gap'] ?? '4px',
                paddingTop: '12px',
            }, children: mainMenuItems.map((item, index) => renderMenuItem(item, `main-${index}`)) }));
    }, [collapsed, theme, mainMenuItems, renderMenuItem]);
    const renderSupportSection = useCallback((isMobile) => {
        if (!supportItem)
            return null;
        let supportClassName;
        if (isMobile) {
            supportClassName = 'expanded';
        }
        else if (collapsed) {
            supportClassName = 'collapsed';
        }
        else {
            supportClassName = 'expanded';
        }
        return (_jsx("div", { className: `${styles['sidebar-support']} ${supportClassName}`, children: renderMenuItem(supportItem, 'support') }));
    }, [supportItem, collapsed, renderMenuItem]);
    const renderDesktopSidebar = () => (_jsxs("aside", { className: getDesktopSidebarClassName(className), style: sidebarStyle, children: [renderSidebarHeader(false), renderSidebarContent(false), renderSupportSection(false)] }));
    const renderMobileSidebar = () => (_jsxs("aside", { className: getMobileSidebarClassName(mobileOverlay), children: [renderSidebarHeader(true), renderSidebarContent(true), renderSupportSection(true)] }));
    return (_jsxs(_Fragment, { children: [isMobileSize && (_jsx("div", { className: `${styles['sidebar-overlay']} ${mobileOverlay ? styles.active : ''}`, onClick: handleMobileClose, onKeyDown: handleOverlayKeyDown, role: 'button', tabIndex: 0, "aria-label": 'Fechar menu' })), !isMobileSize && !hideOnMobile && renderDesktopSidebar(), isMobileSize && renderMobileSidebar()] }));
};
